/**
 * Stable identifiers for pages that participate in Cmd+K alias matching.
 *
 * Aliases live under `quickSearch.aliases.<key>` in the locale files. Each key
 * here MUST exist as a leaf in `quickSearch.aliases` for every supported locale.
 * The literal-typed tuple is what lets `usePageAliases` call `t(key)` with a
 * literal — TS verifies every key against the `Messages` augmentation at the
 * call site, which is how we get a build-time guarantee that an alias entry
 * exists for every page below.
 */
import routeDirectory from '~/navigation/routes'

export const PAGE_ALIAS_KEYS = [
  'accountSettings',
  'agreements',
  'apiKeys',
  'authSettings',
  'batteries',
  'chargeAi',
  'chargers',
  'costExplorer',
  'dashboard',
  'fleetBilling',
  'fleets',
  'generativeDashboard',
  'generators',
  'homeCharging',
  'integrations',
  'locationsOfInterest',
  'manageBatteries',
  'manageChargers',
  'manageSites',
  'manageVehicles',
  'meters',
  'organizationSettings',
  'organizations',
  'overview',
  'panels',
  'paymentTerminals',
  'products',
  'publicCharging',
  'reports',
  'sites',
  'synopLinks',
  'tariffs',
  'theme',
  'userGroups',
  'users',
  'vehicles',
  'workplaceCharging'
] as const

export type PageAliasKey = (typeof PAGE_ALIAS_KEYS)[number]

const { chargeai, chargers, dashboard, generativeDashboard, reports, settings, sites, vehicles } = routeDirectory

/**
 * Maps a nav link `path` to its alias key. Used by `useQuickSearchPages` to
 * attach `keywords` to each `QuickSearchPage`. Routes not present in this map
 * simply produce a page row with no `keywords`, preserving today's behavior.
 *
 * The `?mode=manage` variants for chargers/sites/vehicles are added explicitly
 * in `useQuickSearchPages` (they are not real nav links), so they don't appear
 * here.
 */
export const ROUTE_TO_ALIAS_KEY: Readonly<Partial<Record<string, PageAliasKey>>> = {
  [dashboard.root]: 'dashboard',
  [sites.root]: 'sites',
  [chargers.root]: 'chargers',
  [vehicles.root]: 'vehicles',
  [settings.batteries.root]: 'batteries',
  [settings.generators.root]: 'generators',
  [settings.panels.root]: 'panels',
  [settings.links.root]: 'synopLinks',
  [chargeai.root]: 'chargeAi',
  [generativeDashboard.root]: 'generativeDashboard',
  [reports.root]: 'reports',
  [settings.billingOverview]: 'overview',
  [settings.products.root]: 'products',
  [settings.agreements.root]: 'agreements',
  [settings.paymentTerminals]: 'paymentTerminals',
  [settings.costExplorer]: 'costExplorer',
  [settings.fleetBilling]: 'fleetBilling',
  [settings.homeCharging.root]: 'homeCharging',
  [settings.publicCharging]: 'publicCharging',
  [settings.workplaceCharging]: 'workplaceCharging',
  [settings.auth]: 'authSettings',
  [settings.integrations.root]: 'integrations',
  [settings.orgs]: 'organizations',
  [settings.orgManagement]: 'organizationSettings',
  [settings.themeEditor]: 'theme',
  [settings.userGroups.root]: 'userGroups',
  [settings.users.root]: 'users',
  [settings.apiKeys]: 'apiKeys',
  [settings.fleets.root]: 'fleets',
  [settings.locations]: 'locationsOfInterest',
  [settings.tariffs.root]: 'tariffs',
  [settings.root]: 'accountSettings'
}
